import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Box, Typography, CircularProgress, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import api from '../services/api';
import { HeaderIOS, IOS } from './shared/MenuLayout';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  PointElement, LineElement, ArcElement, Tooltip as CTooltip, Legend, Filler
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, CTooltip, Legend, Filler);

const fmt = n => `S/ ${parseFloat(n||0).toLocaleString('es-PE',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const hoy = () => new Date().toISOString().split('T')[0];
const inicioMes = () => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; };
const COLORS = ['#007AFF','#34C759','#FF9500','#FF3B30','#5E5CE6','#5AC8FA','#FF2D55','#30D158'];

const useCountUp = (target, duration = 850) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    const end = Number(target || 0);
    if (!end) {
      setValue(0);
      return;
    }

    let frame;
    const startTime = performance.now();

    const tick = now => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(end * eased);

      if (progress < 1) frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, duration]);

  return value;
};

const Sel = ({ label, value, onChange, disabled, children, w=160 }) => {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const ref = React.useRef(null);
  const popupRef = React.useRef(null);
  const opciones = React.Children.map(children, ch => ({ value: String(ch.props.value ?? ''), label: ch.props.children })) || [];
  const selLabel = opciones.find(o => o.value === String(value||''))?.label || '';
  useEffect(() => {
    const h = e => {
      const dentroTrigger = ref.current && ref.current.contains(e.target);
      const dentroPopup = popupRef.current && popupRef.current.contains(e.target);
      if (!dentroTrigger && !dentroPopup) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  if(disabled) return (
    <Box sx={{ display:'flex', flexDirection:'column', gap:0.3 }}>
      {label && <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.07em', textTransform:'uppercase' }}>{label}</Typography>}
      <Box sx={{ height:34, px:1.5, display:'flex', alignItems:'center', border:`1px solid ${IOS.separator}`, borderRadius:'8px', bgcolor:IOS.bg, minWidth:w, opacity:0.6 }}>
        <Typography sx={{ fontSize:'0.8rem', color:IOS.label2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{selLabel}</Typography>
      </Box>
    </Box>
  );
  return (
    <Box sx={{ display:'flex', flexDirection:'column', gap:0.3 }}>
      {label && <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.07em', textTransform:'uppercase' }}>{label}</Typography>}
      <Box ref={ref} sx={{ position:'relative', width:w }}>
        <Box onClick={()=>{ if(ref.current) setRect(ref.current.getBoundingClientRect()); setOpen(v=>!v); }}
          sx={{ height:34, px:1.2, pr:3.5, display:'flex', alignItems:'center',
            border:`1px solid ${open?IOS.blue:IOS.separator}`, borderRadius:'8px',
            bgcolor:IOS.surface, cursor:'pointer', userSelect:'none',
            boxShadow:open?`0 0 0 3px ${IOS.blue}18`:'none', transition:'all 0.15s', position:'relative' }}>
          <Typography sx={{ fontSize:'0.8rem', color:IOS.label, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{selLabel}</Typography>
          <Box sx={{ position:'absolute', right:10, top:'50%', transform:`translateY(-50%) rotate(${open?180:0}deg)`, transition:'transform 0.2s' }}>
            <svg width="10" height="6" viewBox="0 0 10 6" fill="none"><path d="M1 1l4 4 4-4" stroke={IOS.label2} strokeWidth="1.5" strokeLinecap="round"/></svg>
          </Box>
        </Box>
        {open && rect && ReactDOM.createPortal(
          <Box sx={{ position:'fixed', top:rect.bottom+4, left:rect.left, zIndex:99999,
            bgcolor:IOS.surface, border:`1px solid ${IOS.separator}`, borderRadius:'12px',
            boxShadow:'0 8px 24px rgba(0,0,0,0.14)', minWidth:rect.width, maxHeight:260, overflowY:'auto' }}>
            {opciones.map(op=>(
              <Box key={op.value} onMouseDown={e=>{ e.preventDefault(); onChange({target:{value:op.value}}); setOpen(false); }}
                sx={{ px:1.5, py:0.9, cursor:'pointer', bgcolor:String(value||'')===op.value?`${IOS.blue}08`:'transparent', '&:hover':{ bgcolor:IOS.bg } }}>
                <Typography sx={{ fontSize:'0.8rem', color:String(value||'')===op.value?IOS.blue:IOS.label, fontWeight:String(value||'')===op.value?700:400 }}>{op.label}</Typography>
              </Box>
            ))}
          </Box>, document.body
        )}
      </Box>
    </Box>
  );
};

const KpiCard = ({ label, valor, sub, color, icon, money = true }) => {
  const animado = useCountUp(valor, 850);

  return (
    <Box sx={{ bgcolor:IOS.surface, borderRadius:'16px', p:2.2, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', border:`1px solid ${IOS.separator}` }}>
      <Box sx={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:1 }}>
        <Box sx={{ minWidth:0 }}>
          <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.07em', textTransform:'uppercase', mb:0.8 }}>{label}</Typography>
          <Typography sx={{ fontSize:'1.6rem', fontWeight:800, color, lineHeight:1, letterSpacing:0, mb:0.4, fontVariantNumeric:'tabular-nums' }}>
            {money
              ? fmt(animado)
              : Math.round(animado).toLocaleString('es-PE')
            }
          </Typography>
          {sub && <Typography sx={{ fontSize:'0.7rem', color:IOS.label2 }}>{sub}</Typography>}
        </Box>
        <Box sx={{ width:44, height:44, borderRadius:'12px', bgcolor:`${color}12`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{icon}</Box>
      </Box>
    </Box>
  );
};

const RankingBar = ({ items, campoLabel, color }) => {
  const max = Math.max(...items.map(i=>i.total),1);
  return (
    <Box sx={{ display:'flex', flexDirection:'column', gap:1.4 }}>
      {items.map((it,i)=>{
        const pct = (it.total/max)*100;
        return (
          <Box key={i}>
            <Box sx={{ display:'flex', justifyContent:'space-between', mb:0.4 }}>
              <Typography sx={{ fontSize:'0.78rem', color:IOS.label, fontWeight:600 }}>{i+1}. {it[campoLabel]}</Typography>
              <Typography sx={{ fontSize:'0.78rem', fontWeight:700, color }}>{fmt(it.total)}</Typography>
            </Box>
            <Box sx={{ height:6, bgcolor:`${color}15`, borderRadius:'20px', overflow:'hidden' }}>
              <Box sx={{ width:`${pct}%`, height:'100%', bgcolor:color, borderRadius:'20px', transition:'width 0.8s ease' }} />
            </Box>
          </Box>
        );
      })}
      {items.length===0 && <Typography sx={{ fontSize:'0.8rem', color:IOS.label2, textAlign:'center', py:3 }}>Sin datos</Typography>}
    </Box>
  );
};

const DIAS_SEMANA = ['D','L','M','M','J','V','S'];
const MESES_LARGO = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const toYMD = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
const parseYMD = (s) => { if(!s) return new Date(); const [y,m,d] = s.split('-').map(Number); return new Date(y, m-1, d); };

const DatePickerIOS = ({ value, onChange, label, w=150 }) => {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const ref = React.useRef(null);
  const popupRef = React.useRef(null);
  const selDate = value ? parseYMD(value) : null;
  const [viewMonth, setViewMonth] = useState(selDate ? selDate.getMonth() : new Date().getMonth());
  const [viewYear, setViewYear] = useState(selDate ? selDate.getFullYear() : new Date().getFullYear());

  useEffect(() => {
    const h = e => {
      const dentroTrigger = ref.current && ref.current.contains(e.target);
      const dentroPopup = popupRef.current && popupRef.current.contains(e.target);
      if (!dentroTrigger && !dentroPopup) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleOpen = () => {
    if (ref.current) setRect(ref.current.getBoundingClientRect());
    if (selDate) { setViewMonth(selDate.getMonth()); setViewYear(selDate.getFullYear()); }
    setOpen(v=>!v);
  };

  const cambiarMes = (delta) => {
    let m = viewMonth + delta, y = viewYear;
    if (m < 0) { m = 11; y--; } else if (m > 11) { m = 0; y++; }
    setViewMonth(m); setViewYear(y);
  };

  const elegirDia = (dia) => { onChange(toYMD(new Date(viewYear, viewMonth, dia))); setOpen(false); };

  const hoy = new Date();
  const primerDiaSemana = new Date(viewYear, viewMonth, 1).getDay();
  const totalDias = new Date(viewYear, viewMonth+1, 0).getDate();
  const celdas = [...Array(primerDiaSemana).fill(null), ...Array.from({length:totalDias},(_,i)=>i+1)];
  const labelFecha = selDate ? `${String(selDate.getDate()).padStart(2,'0')}/${String(selDate.getMonth()+1).padStart(2,'0')}/${selDate.getFullYear()}` : '';

  return (
    <Box sx={{ display:'flex', flexDirection:'column', gap:0.3 }}>
      {label && <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.07em', textTransform:'uppercase' }}>{label}</Typography>}
      <Box ref={ref} sx={{ position:'relative', width:w }}>
        <Box onClick={handleOpen}
          sx={{ height:34, px:1.2, display:'flex', alignItems:'center', gap:0.8,
            border:`1px solid ${open?IOS.blue:IOS.separator}`, borderRadius:'8px',
            bgcolor:IOS.surface, cursor:'pointer', userSelect:'none',
            boxShadow:open?`0 0 0 3px ${IOS.blue}18`:'none', transition:'all 0.15s' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={IOS.label2} strokeWidth="2" strokeLinecap="round">
            <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <Typography sx={{ fontSize:'0.8rem', color:IOS.label, flex:1, whiteSpace:'nowrap' }}>{labelFecha}</Typography>
        </Box>

        {open && rect && ReactDOM.createPortal(
          <Box ref={popupRef} sx={{ position:'fixed', top:rect.bottom+4, left:rect.left, zIndex:99999,
            bgcolor:IOS.surface, border:`1px solid ${IOS.separator}`, borderRadius:'14px',
            boxShadow:'0 8px 24px rgba(0,0,0,0.16)', width:260, p:1.5 }}>

            <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:1 }}>
              <Box component="button" onClick={()=>cambiarMes(-1)}
                sx={{ width:26, height:26, borderRadius:'8px', border:'none', bgcolor:IOS.bg, cursor:'pointer',
                  display:'flex', alignItems:'center', justifyContent:'center', '&:hover':{ bgcolor:IOS.separator } }}>
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="3" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
              </Box>
              <Typography sx={{ fontSize:'0.82rem', fontWeight:700, color:IOS.label }}>{MESES_LARGO[viewMonth]} {viewYear}</Typography>
              <Box component="button" onClick={()=>cambiarMes(1)}
                sx={{ width:26, height:26, borderRadius:'8px', border:'none', bgcolor:IOS.bg, cursor:'pointer',
                  display:'flex', alignItems:'center', justifyContent:'center', '&:hover':{ bgcolor:IOS.separator } }}>
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="3" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
              </Box>
            </Box>

            <Box sx={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', mb:0.5 }}>
              {DIAS_SEMANA.map((d,i)=>(<Typography key={i} sx={{ fontSize:'0.62rem', fontWeight:700, color:IOS.label2, textAlign:'center' }}>{d}</Typography>))}
            </Box>

            <Box sx={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:'2px' }}>
              {celdas.map((dia,i)=>{
                if (dia===null) return <Box key={i} />;
                const esHoy = dia===hoy.getDate() && viewMonth===hoy.getMonth() && viewYear===hoy.getFullYear();
                const esSel = selDate && dia===selDate.getDate() && viewMonth===selDate.getMonth() && viewYear===selDate.getFullYear();
                return (
                  <Box key={i} onClick={()=>elegirDia(dia)}
                    sx={{ height:30, display:'flex', alignItems:'center', justifyContent:'center', borderRadius:'8px', cursor:'pointer',
                      bgcolor:esSel?IOS.blue:'transparent',
                      border:esHoy&&!esSel?`1.5px solid ${IOS.blue}`:'none',
                      '&:hover':{ bgcolor:esSel?IOS.blue:IOS.bg } }}>
                    <Typography sx={{ fontSize:'0.78rem', fontWeight:esSel?700:500, color:esSel?'white':IOS.label }}>{dia}</Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>, document.body
        )}
      </Box>
    </Box>
  );
};

const Reportes = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const ueId = user?.ueIdEstablecimiento || user?.UeIdEstablecimiento || 0;
  const nf = user?.nombreCompleto || user?.usuario || '';
  const handleLogout = () => { authService.logout(); navigate('/login'); };

  const [privRep, setPrivRep] = useState(null);
  useEffect(()=>{
    api.get('/dashboard/permisos').then(r=>{
      if(r.data.success){
        const op = r.data.opciones.find(o=>(o.permiso||'').toLowerCase().includes('reporte'));
        if(op) setPrivRep(op.privilegio||'Solo lectura');
      }
    }).catch(()=>{});
  },[]);
  const permisosListos = privRep !== null;
const esTOTAL = privRep === 'Total';
const idCajero = esTOTAL ? '' : (user?.usuario || '');

  const [kpis, setKpis] = useState(null);
  const [loadKpi, setLoadKpi] = useState(true);
  const [fechaIni, setFechaIni] = useState(inicioMes());
  const [fechaFin, setFechaFin] = useState(hoy());
  const [cajas, setCajas] = useState([]);
  const [cajaSel, setCajaSel] = useState('');
  const [modoTend, setModoTend] = useState('DIA');
  const [tendencia, setTendencia] = useState([]);
  const [topCajeros, setTopCajeros] = useState([]);
  const [porCaja, setPorCaja] = useState([]);
  const [porFp, setPorFp] = useState([]);
  const [loadDatos, setLoadDatos] = useState(true);

  useEffect(()=>{
    if(!ueId) return;
    api.get('/caja/cajas-ventas', { params:{ idUe: ueId } })
      .then(r=>{ if(r.data.success) setCajas(r.data.data||[]); })
      .catch(console.error);
  },[ueId]);

  useEffect(()=>{
  if(!ueId || !permisosListos) return;

  let activo = true;
  setLoadKpi(true);

  api.get('/caja/reportes/kpis', { params:{ ueId, idCajero } })
    .then(r=>{
      if(activo && r.data.success) setKpis(r.data.data);
    })
    .catch(console.error)
    .finally(()=>{
      if(activo) setLoadKpi(false);
    });

  return () => { activo = false; };
},[ueId, idCajero, permisosListos]);

  useEffect(()=>{
  if(!ueId || !permisosListos) return;

  let activo = true;

  api.get('/caja/reportes/tendencia', { params:{ ueId, idCajero, modo:modoTend } })
    .then(r=>{
      if(activo && r.data.success) setTendencia(r.data.data || []);
    })
    .catch(console.error);

  return () => { activo = false; };
},[ueId, idCajero, modoTend, permisosListos]);

  useEffect(()=>{
  if(!ueId || !permisosListos) return;

  let activo = true;
  setLoadDatos(true);

  Promise.all([
    api.get('/caja/reportes/top-cajeros', {
      params:{
        ueId,
        idCajero,
        ...(cajaSel ? { idCaja:cajaSel } : {}),
        fechaInicio:fechaIni,
        fechaFin,
        topN:10
      }
    }),
    api.get('/caja/reportes/por-caja', { params:{ ueId, idCajero, fechaInicio:fechaIni, fechaFin } }),
    api.get('/caja/reportes/por-forma-pago', { params:{ ueId, idCajero, fechaInicio:fechaIni, fechaFin } }),
  ]).then(([rC,rCaja,rFp])=>{
    if(!activo) return;
    if(rC.data.success) setTopCajeros(rC.data.data||[]);
    if(rCaja.data.success) setPorCaja(rCaja.data.data||[]);
    if(rFp.data.success) setPorFp(rFp.data.data||[]);
  }).catch(console.error).finally(()=>{
    if(activo) setLoadDatos(false);
  });

  return () => { activo = false; };
},[ueId, idCajero, fechaIni, fechaFin, cajaSel, permisosListos]);

  const MESES_ABR = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];

  const etiquetaTend = (periodoStr) => {
    if (modoTend === 'MES') {
      const [y,m] = periodoStr.split('-');
      const mes = MESES_ABR[Number(m)-1] || m;
      return m === '12' ? `${mes}'${y.slice(2)}` : mes;
    }
    const [y,m,d] = periodoStr.split('-');
    return m === '12' ? `${d}/${m}/${y.slice(2)}` : `${d}/${m}`;
  };

  const [tipoTend, setTipoTend] = useState('line');
  const baseOpts = { responsive:true, maintainAspectRatio:false };

  const esBarra = tipoTend==='bar' || tipoTend==='stacked';
  const chartTend = tipoTend==='stacked'
    ? {
        labels: tendencia.map(t=>etiquetaTend(t.periodo)),
        datasets:[
          { label:'Ventas', data: tendencia.map(t=>t.total), backgroundColor:`${IOS.green}CC`, borderRadius:6, stack:'s', datalabels:{display:false} },
          { label:'Anuladas', data: tendencia.map(t=>t.totalAnuladas||0), backgroundColor:`${IOS.red}CC`, borderRadius:6, stack:'s', datalabels:{display:false} },
        ]
      }
    : {
        labels: tendencia.map(t=>etiquetaTend(t.periodo)),
        datasets:[{
          label:'Recaudación', data: tendencia.map(t=>t.total),
          borderColor: IOS.blue,
          backgroundColor: tipoTend==='bar' ? `${IOS.blue}CC` : `${IOS.blue}15`,
          borderRadius: tipoTend==='bar' ? 6 : 0,
          tension:0.4, fill: tipoTend==='line', pointRadius:3, pointBackgroundColor:IOS.blue, borderWidth:2,
          datalabels:{ display:false },
        }]
      };

  const chartTendOpts = {
  ...baseOpts,
  animation:{
    duration:750,
    easing:'easeOutQuart'
  },
  transitions:{
    active:{
      animation:{
        duration:250
      }
    }
  },
  plugins:{
      legend:{ display: tipoTend==='stacked', position:'bottom', labels:{boxWidth:10,font:{size:10},color:IOS.label2} },
      tooltip:{callbacks:{label:ctx=>`${ctx.dataset.label}: ${fmt(ctx.parsed.y)}`}},
    },
    scales:{
      x:{ grid:{display:false}, border:{display:false}, ticks:{color:IOS.label2, font:{size:10}, maxTicksLimit:15}, stacked: tipoTend==='stacked' },
      y:{ grid:{color:'rgba(60,60,67,0.05)'}, border:{display:false}, ticks:{color:IOS.label2, font:{size:10}, callback:v=>`S/${(v/1000).toFixed(0)}k`}, stacked: tipoTend==='stacked' },
    },
  };
  const chartFp = {
    labels: porFp.map(f=>f.formaPago),
    datasets:[{ data: porFp.map(f=>f.total), backgroundColor:COLORS, borderWidth:0, hoverOffset:6 }]
  };

  return (
    <Box sx={{ minHeight:'100vh', bgcolor:IOS.bg }}>
      <HeaderIOS titulo="Reportes" subtitulo="Caja"
        onHome={()=>navigate('/caja')} onLogout={handleLogout}
        nombreCompleto={nf} avatarLetra={nf.charAt(0).toUpperCase()} />

      <Box sx={{ maxWidth:1600, mx:'auto', px:{ xs:1.5, sm:2.6 }, pt:2.5, pb:6 }}>

        {/* KPIs */}
        <Box sx={{ display:'grid', gridTemplateColumns:{ xs:'1fr 1fr', sm:'repeat(3,1fr)', lg:'repeat(5,1fr)' }, gap:1.5, mb:2.5 }}>
          {loadKpi
            ? [0,1,2,3,4].map(i=><Box key={i} sx={{ bgcolor:IOS.surface, borderRadius:'16px', p:2.2, height:90, display:'flex', alignItems:'center', justifyContent:'center', border:`1px solid ${IOS.separator}` }}><CircularProgress size={18} sx={{ color:IOS.blue }}/></Box>)
            : <>
                <KpiCard label="Recaudación Hoy" valor={kpis?.recaudacionHoy} sub={`${kpis?.cantidadHoy||0} ventas`} color={IOS.blue}
                  icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>} />
                <KpiCard label="Recaudación del Mes" valor={kpis?.recaudacionMes} sub={`${kpis?.cantidadMes||0} ventas`} color={IOS.green}
                  icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={IOS.green} strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>} />
                <KpiCard label="Recaudación del Año" valor={kpis?.recaudacionAnio} sub={`${kpis?.cantidadAnio||0} ventas`} color={IOS.purple}
                  icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={IOS.purple} strokeWidth="1.8" strokeLinecap="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>} />
                <KpiCard label="Anulaciones Hoy" valor={kpis?.anulacionesHoy||0} sub={fmt(kpis?.montoAnuladoHoy)} color={IOS.red} money={false}
                  icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={IOS.red} strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>} />
                <KpiCard label="Anulaciones del Mes" valor={kpis?.anulacionesMes||0} sub={fmt(kpis?.montoAnuladoMes)} color={IOS.orange} money={false}
                  icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={IOS.orange} strokeWidth="1.8" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/></svg>} />
              </>
          }
        </Box>

        {/* Tendencia */}
        <Box sx={{ bgcolor:IOS.surface, borderRadius:'16px', p:2.5, mb:2.5, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', border:`1px solid ${IOS.separator}` }}>
          <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', mb:2 }}>
            <Box>
              <Typography sx={{ fontWeight:700, fontSize:'0.9rem', color:IOS.label }}>Tendencia de recaudación</Typography>
              <Typography sx={{ fontSize:'0.7rem', color:IOS.label2 }}>{modoTend==='DIA'?'Últimos 30 días':'Últimos 12 meses'}</Typography>
            </Box>
            <Box sx={{ display:'flex', gap:1, alignItems:'center' }}>
              <Box sx={{ display:'flex', gap:0.4, bgcolor:IOS.bg, borderRadius:'10px', p:0.4 }}>
                {['DIA','MES'].map(m=>(
                  <Box key={m} onClick={()=>setModoTend(m)} sx={{ px:1.5, py:0.6, borderRadius:'8px', cursor:'pointer',
                    bgcolor:modoTend===m?IOS.surface:'transparent', boxShadow:modoTend===m?'0 1px 3px rgba(0,0,0,0.1)':'none' }}>
                    <Typography sx={{ fontSize:'0.74rem', fontWeight:modoTend===m?700:500, color:modoTend===m?IOS.blue:IOS.label2 }}>{m==='DIA'?'Día':'Mes'}</Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ display:'flex', gap:0.4, bgcolor:IOS.bg, borderRadius:'10px', p:0.4 }}>
                {[
                  {key:'line',    tip:'Línea',    icon:<path d="M3 17l4-8 4 4 4-6 4 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>},
                  {key:'bar',     tip:'Barras',    icon:<><rect x="3" y="10" width="4" height="8" rx="1" fill="currentColor" opacity=".9"/><rect x="10" y="6" width="4" height="12" rx="1" fill="currentColor" opacity=".9"/><rect x="17" y="3" width="4" height="15" rx="1" fill="currentColor" opacity=".9"/></>},
                  {key:'stacked', tip:'Apiladas',  icon:<><rect x="3" y="13" width="4" height="5" rx="1" fill="currentColor" opacity=".5"/><rect x="3" y="8"  width="4" height="5" rx="1" fill="currentColor"/><rect x="10" y="10" width="4" height="8" rx="1" fill="currentColor" opacity=".5"/><rect x="10" y="5" width="4" height="5" rx="1" fill="currentColor"/><rect x="17" y="11" width="4" height="7" rx="1" fill="currentColor" opacity=".5"/><rect x="17" y="6" width="4" height="5" rx="1" fill="currentColor"/></>},
                ].map(({key,tip,icon})=>(
                  <Tooltip key={key} title={tip} placement="top">
                    <Box onClick={()=>setTipoTend(key)}
                      sx={{ width:30, height:30, borderRadius:'8px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.15s',
                        bgcolor:tipoTend===key?IOS.surface:'transparent',
                        boxShadow:tipoTend===key?'0 1px 3px rgba(0,0,0,0.12)':'none',
                        color:tipoTend===key?IOS.blue:IOS.label2 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24">{icon}</svg>
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </Box>
          </Box>
          <Box key={`${tipoTend}-${modoTend}`} sx={{ height:240, animation:'fadeInChart 0.35s ease' }}>
            {tendencia.length===0
              ? <Box sx={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}><Typography sx={{ color:IOS.label2, fontSize:'0.85rem' }}>Sin datos</Typography></Box>
              : esBarra ? <Bar data={chartTend} options={chartTendOpts}/> : <Line data={chartTend} options={chartTendOpts}/>
            }
          </Box>
        </Box>

        {/* Selector de período — aplica solo a lo de abajo */}
        <Box sx={{ display:'flex', alignItems:'center', gap:1.5, mb:1.5, flexWrap:'wrap' }}>
          <Typography sx={{ fontSize:'0.78rem', fontWeight:700, color:IOS.label2 }}>Período a mostrar:</Typography>
          <DatePickerIOS value={fechaIni} onChange={setFechaIni} w={140} />
          <Typography sx={{ fontSize:'0.78rem', color:IOS.label2 }}>—</Typography>
          <DatePickerIOS value={fechaFin} onChange={setFechaFin} w={140} />
          {!esTOTAL && (
            <Box sx={{ ml:'auto', display:'flex', alignItems:'center', gap:0.8, px:1.5, py:0.6, bgcolor:IOS.bg, borderRadius:'20px', border:`1px solid ${IOS.separator}` }}>
              <Box sx={{ width:7, height:7, borderRadius:'50%', bgcolor:IOS.orange }} />
              <Typography sx={{ fontSize:'0.72rem', fontWeight:600, color:IOS.label2 }}>Viendo solo tus ventas — {nf}</Typography>
            </Box>
          )}
        </Box>

        {/* Rankings + forma de pago */}
        <Box sx={{ display:'grid', gridTemplateColumns:{ xs:'1fr', lg:'1fr 1fr 1fr' }, gap:2 }}>
          <Box sx={{ bgcolor:IOS.surface, borderRadius:'16px', p:2.5, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', border:`1px solid ${IOS.separator}` }}>
            <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', mb:2, gap:1 }}>
              <Box>
                <Typography sx={{ fontWeight:700, fontSize:'0.88rem', color:IOS.label, mb:0.3 }}>Top Cajeros</Typography>
                <Typography sx={{ fontSize:'0.7rem', color:IOS.label2 }}>{cajaSel ? 'Filtrado por caja' : 'Todas las cajas'}</Typography>
              </Box>
              <Sel value={cajaSel} onChange={e=>setCajaSel(e.target.value)} w={140}>
                <option value="">Todas</option>
                {cajas.map(c=><option key={c.idCaja} value={c.idCaja}>{c.nombre}</option>)}
              </Sel>
            </Box>
            {loadDatos ? <Box sx={{ py:6, textAlign:'center' }}><CircularProgress size={18} sx={{ color:IOS.blue }}/></Box> : <RankingBar items={topCajeros} campoLabel="cajero" color={IOS.blue} />}
          </Box>
          <Box sx={{ bgcolor:IOS.surface, borderRadius:'16px', p:2.5, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', border:`1px solid ${IOS.separator}` }}>
            <Typography sx={{ fontWeight:700, fontSize:'0.88rem', color:IOS.label, mb:0.3 }}>Recaudación por Caja</Typography>
            <Typography sx={{ fontSize:'0.7rem', color:IOS.label2, mb:2 }}>Cuál caja recauda más</Typography>
            {loadDatos ? <Box sx={{ py:6, textAlign:'center' }}><CircularProgress size={18} sx={{ color:IOS.green }}/></Box> : (
              <>
                <RankingBar items={porCaja} campoLabel="caja" color={IOS.green} />
                {porCaja.length>0 && (
                  <Box sx={{ mt:1.5, pt:1.5, borderTop:`1px solid ${IOS.separator}`, display:'flex', justifyContent:'space-between' }}>
                    <Typography sx={{ fontSize:'0.78rem', fontWeight:700, color:IOS.label2 }}>Total</Typography>
                    <Typography sx={{ fontSize:'0.85rem', fontWeight:800, color:IOS.green }}>{fmt(porCaja.reduce((s,c)=>s+c.total,0))}</Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
          <Box sx={{ bgcolor:IOS.surface, borderRadius:'16px', p:2.5, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', border:`1px solid ${IOS.separator}` }}>
            <Typography sx={{ fontWeight:700, fontSize:'0.88rem', color:IOS.label, mb:0.3 }}>Por Forma de Pago</Typography>
            <Typography sx={{ fontSize:'0.7rem', color:IOS.label2, mb:2 }}>Distribución del período</Typography>
            <Box sx={{ height:200 }}>
              {loadDatos
                ? <Box sx={{ py:6, textAlign:'center' }}><CircularProgress size={18} sx={{ color:IOS.orange }}/></Box>
                : porFp.length===0
                  ? <Box sx={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}><Typography sx={{ color:IOS.label2, fontSize:'0.85rem' }}>Sin datos</Typography></Box>
                  : <Doughnut data={chartFp} options={{...baseOpts, cutout:'60%', plugins:{legend:{display:true,position:'bottom',labels:{font:{size:10},boxWidth:10,padding:8}}, tooltip:{callbacks:{label:ctx=>fmt(ctx.parsed)}}}}}/>
              }
            </Box>
          </Box>
        </Box>
      </Box>

      <style>{`
        @keyframes fadeInChart {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Box>
  );
};

export default Reportes;